import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExploreIcon from '@mui/icons-material/Explore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { Navigate, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { deleteSavedTour, getSavedToursForUser } from '../services/savedTours';
import { APP_THEME } from '../theme';
import { SavedTour } from '../types/tour';

const getPasswordErrorMessage = (error: unknown): string => {
  const code = (error as { code?: string })?.code;

  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Current password is incorrect.';
    case 'auth/weak-password':
      return 'New password must be at least 6 characters.';
    default:
      return 'Unable to update password. Please try again.';
  }
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [savedTours, setSavedTours] = useState<SavedTour[]>([]);
  const [loadingTours, setLoadingTours] = useState(true);
  const [toursError, setToursError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const loadSavedTours = useCallback(async () => {
    if (!user) return;

    setLoadingTours(true);
    setToursError(null);
    try {
      const tours = await getSavedToursForUser(user.uid);
      setSavedTours(tours);
    } catch (error) {
      console.error('Error loading saved tours:', error);
      setToursError('Failed to load saved tours.');
    } finally {
      setLoadingTours(false);
    }
  }, [user]);

  useEffect(() => {
    loadSavedTours();
  }, [loadSavedTours]);

  const handlePasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);

    if (!user?.email) return;

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setUpdatingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser!, credential);
      await updatePassword(auth.currentUser!, newPassword);

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('Password updated successfully.');
    } catch (error) {
      setPasswordError(getPasswordErrorMessage(error));
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleDeleteTour = async (tourId: string) => {
    if (!user) return;

    try {
      await deleteSavedTour(user.uid, tourId);
      setSavedTours((prev) => prev.filter((tour) => tour.id !== tourId));
    } catch (error) {
      console.error('Error deleting tour:', error);
      setToursError('Failed to delete tour.');
    }
  };

  const handleViewTour = (tour: SavedTour) => {
    navigate('/', { state: { loadedTour: tour } });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: APP_THEME.colors.primary }} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: APP_THEME.colors.pageBg,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AppHeader />

      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          pt: { xs: '76px', sm: '95px' },
          pb: 6,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: APP_THEME.colors.text,
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
          }}
        >
          Your Dashboard
        </Typography>
        <Typography sx={{ color: APP_THEME.colors.text, opacity: 0.75, mb: 4 }}>
          Manage your account and revisit saved walking tours.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 700, color: APP_THEME.colors.text, mb: 2 }}>
          Saved Walking Tours
        </Typography>

        {toursError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {toursError}
          </Alert>
        )}

        {loadingTours ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: APP_THEME.colors.primary }} />
          </Box>
        ) : savedTours.length === 0 ? (
          <Box
            sx={{
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              bgcolor: 'white',
              border: `1px solid ${APP_THEME.colors.text}14`,
              mb: 4,
            }}
          >
            <ExploreIcon sx={{ fontSize: 48, color: APP_THEME.colors.primary, mb: 1 }} />
            <Typography sx={{ color: APP_THEME.colors.text, fontWeight: 600, mb: 1 }}>
              No saved tours yet
            </Typography>
            <Typography sx={{ color: APP_THEME.colors.text, opacity: 0.7, mb: 2 }}>
              Generate a tour and tap the + button to keep it here.
            </Typography>
            <Button
              onClick={() => navigate('/')}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: APP_THEME.colors.primary,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#ff5252', boxShadow: 'none' },
              }}
            >
              Create a Tour
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
            {savedTours.map((tour) => (
              <Accordion
                key={tour.id}
                disableGutters
                elevation={0}
                sx={{
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  bgcolor: 'white',
                  border: `1px solid ${APP_THEME.colors.text}14`,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: APP_THEME.colors.text, flexShrink: 0 }} />}
                  sx={{
                    minHeight: 56,
                    px: { xs: 2, sm: 2.5 },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      my: 1,
                      mr: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minWidth: 0,
                      gap: 1,
                    }}
                  >
                    <Typography
                      title={tour.tourTitle}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 600,
                        color: APP_THEME.colors.text,
                        fontSize: { xs: '0.95rem', sm: '1.05rem' },
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        px: { xs: 0.5, sm: 1 },
                      }}
                    >
                      {tour.tourTitle}
                    </Typography>
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteTour(tour.id);
                      }}
                      aria-label="Delete saved tour"
                      size="small"
                      sx={{ color: APP_THEME.colors.primary, flexShrink: 0 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 3 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={`Duration: ${tour.duration}`}
                      size="small"
                      sx={{ bgcolor: `${APP_THEME.colors.text}0A`, fontWeight: 500 }}
                    />
                    {tour.city && (
                      <Chip
                        icon={<LocationOnIcon />}
                        label={tour.city}
                        size="small"
                        sx={{ bgcolor: `${APP_THEME.colors.primary}14`, fontWeight: 500 }}
                      />
                    )}
                    <Chip
                      label={`${tour.tourDetails.length} stops`}
                      size="small"
                      sx={{ bgcolor: `${APP_THEME.colors.text}0A`, fontWeight: 500 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {tour.tourDetails.slice(0, 3).map((stop, index) => (
                      <Box
                        key={`${tour.id}-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: `${APP_THEME.colors.text}06`,
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: APP_THEME.colors.primary,
                            color: 'white',
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: APP_THEME.colors.text }}>
                          {stop.pointOfInterest}
                        </Typography>
                      </Box>
                    ))}
                    {tour.tourDetails.length > 3 && (
                      <Typography sx={{ color: APP_THEME.colors.text, opacity: 0.7, pl: 1 }}>
                        +{tour.tourDetails.length - 3} more stops
                      </Typography>
                    )}
                  </Box>

                  {tour.savedAt && (
                    <Typography
                      variant="body2"
                      sx={{ color: APP_THEME.colors.text, opacity: 0.6, mb: 2 }}
                    >
                      Saved {tour.savedAt.toLocaleDateString()}
                    </Typography>
                  )}

                  <Button
                    onClick={() => handleViewTour(tour)}
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      bgcolor: APP_THEME.colors.primary,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#ff5252', boxShadow: 'none' },
                    }}
                  >
                    View Tour
                  </Button>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            borderRadius: '12px !important',
            overflow: 'hidden',
            bgcolor: 'white',
            border: `1px solid ${APP_THEME.colors.text}14`,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: APP_THEME.colors.text }} />}
            sx={{
              minHeight: 56,
              px: { xs: 1.5, sm: 2 },
              '& .MuiAccordionSummary-content': { my: 1 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockOutlinedIcon sx={{ color: APP_THEME.colors.primary, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: APP_THEME.colors.text }}>
                Change Password
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
            <Box component="form" onSubmit={handlePasswordChange}>
              <TextField
                fullWidth
                label="Current password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
              />

              {passwordError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {passwordError}
                </Alert>
              )}
              {passwordMessage && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  {passwordMessage}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                disabled={updatingPassword}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  bgcolor: APP_THEME.colors.primary,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#ff5252', boxShadow: 'none' },
                }}
              >
                {updatingPassword ? <CircularProgress size={22} color="inherit" /> : 'Update Password'}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Container>

      <Footer onLinkClick={() => undefined} />
    </Box>
  );
};

export default Dashboard;
